import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import i18n from '../../translation/i18n';
import IssueGuide from '../IssueGuide';
import { IUser } from '../../atoms';
import { apiGet } from '../../services/_common';
import SkeletonTable from '../SkeletonTable';
import { ERROR } from '../../_constants';
import { Doughnut } from 'react-chartjs-2';

// 현장 정보
export const SiteInfo = ({ name, userInfo, refetchInterval }: { name: string; userInfo: IUser; refetchInterval?: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  dayjs.locale(i18n.language === 'en-US' ? 'en' : 'ko');
  const { t } = useTranslation();
  const { hCd, sCd } = userInfo;
  const todayjs = dayjs();
  let format2 = todayjs.format(`MM월 DD일 dddd`);
  if (i18n.language === 'en-US') format2 = todayjs.locale('en').format(`MMMM D dddd`);
  else format2 = todayjs.locale('ko').format(`MM월 DD일 dddd`);

  const {
    data: siteInfoData,
    isLoading: isLoadingSiteInfoData,
    isError: isErrorSiteInfoData,
    refetch: refetchSiteInfoData,
  } = useQuery(['TotalInfoNewWorkerGet', userInfo], () => fetchSiteInfoData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }), // 30분
  });

  // 현장 정보 조회
  const fetchSiteInfoData = async () => {
    try {
      const req = { hCd: userInfo.hCd, sCd: userInfo.sCd };
      const res = await apiGet({ path: '/site/info', req });
      if (res.status === 200) {
        return res.data.data.siteInfo;
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickRefresh = () => {
    refetchSiteInfoData();
  };

  const getPercentage = () => {
    if (!siteInfoData?.sDate || !siteInfoData?.fDate) return 0;

    // 종료일자가 이미 지났으면 100%
    if (dayjs(siteInfoData?.fDate).isBefore(todayjs)) return 100;

    const startDatejs = dayjs(siteInfoData?.sDate);
    const endDatejs = dayjs(siteInfoData?.fDate);
    const today = dayjs();

    // 전체 기간
    const totalDays = endDatejs.diff(startDatejs, 'day');
    // 현재까지 경과한 기간
    const elapsedDays = today.diff(startDatejs, 'day');

    // 진행률 계산 (0~100%) 및 소수점 첫째자리 제거
    const percentage = Math.floor(Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100)));

    return percentage;
  };

  const data = {
    datasets: [
      {
        data: [getPercentage(), 100 - getPercentage()],
        backgroundColor: ['#60a5fa', '#3e98ff30'],
        borderColor: ['transparent', 'transparent'],
      },
    ],
    hoverOffset: 4,
  };

  const options = {
    cutout: '65%',
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1,
    plugins: {
      tooltip: { enabled: false },
    },
  };

  const loading = isLoadingSiteInfoData || !siteInfoData;

  if (isErrorSiteInfoData) return <IssueGuide text={ERROR} isImg={false} />;
  return (
    <div className='siteInfo'>
      <div className='widget-header'>
        <div className='widget-title flex-between'>
          <span>{t(name)}</span>
          {!isSiteDashboard && (
            <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
              <span className='material-symbols-rounded'>refresh</span>
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <SkeletonTable rows={1} />
      ) : (
        <div className='widget-body'>
          <div className='chart-container'>
            <div className='chart-wrapper' style={{ padding: '1rem' }}>
              <div className='chart-icon'>
                <span className='material-symbols-rounded'>construction</span>
              </div>
              <Doughnut data={data} options={options} />
            </div>
            <div className='percentage'>
              <span className='emphasize'>{getPercentage()}</span>%
            </div>
            <div className='chart-title'>{t('전체 진행률')}</div>
          </div>
          <div className='infoBox-container' style={{ flex: 0.5, width: '100%' }}>
            <div className='infoBox flex-basic'>
              <div className='blue small'>
                <span className='material-symbols-rounded'>location_city</span>
              </div>
              <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
                {!isSiteDashboard && <div className='infoBox-label'>{t('현장명')}</div>}
                <div className={!isSiteDashboard ? 'infoBox-value' : ''}>{siteInfoData?.sName}</div>
                {isSiteDashboard && t('현장명')}
              </div>
            </div>
            <div className='infoBox flex-basic'>
              <div className='green small'>
                <span className='material-symbols-rounded'>location_on</span>
              </div>
              <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
                {!isSiteDashboard && <div className='infoBox-label'>{t('주소')}</div>}
                <div className={!isSiteDashboard ? 'infoBox-value' : ''}>{`${siteInfoData?.address1} ${siteInfoData?.address2}`}</div>
                {isSiteDashboard && t('주소')}
              </div>
            </div>
            <div className='infoBox flex-basic'>
              <div className='violet small'>
                <span className='material-symbols-rounded'>construction</span>
              </div>
              <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
                {!isSiteDashboard && <div className='infoBox-label'>{t('공사 유형')}</div>}
                <div className={!isSiteDashboard ? 'infoBox-value' : ''}>{siteInfoData?.infraName}</div>
                {isSiteDashboard && t('공사 유형')}
              </div>
            </div>
            <div className='infoBox flex-basic'>
              <div className='amber small'>
                <span className='material-symbols-rounded'>task_alt</span>
              </div>
              <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
                {!isSiteDashboard && <div className='infoBox-label'>{t('운영 상태')}</div>}
                <div className={!isSiteDashboard ? 'infoBox-value' : ''}>{siteInfoData?.sStatus === 'Y' ? t('운영중') : t('종료')}</div>
                {isSiteDashboard && t('운영 상태')}
              </div>
            </div>
            <div className='infoBox flex-basic'>
              <div className='orange small'>
                <span className='material-symbols-rounded'>calendar_month</span>
              </div>
              <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
                {!isSiteDashboard && <div className='infoBox-label'>{t('공사 기간')}</div>}
                <div className={!isSiteDashboard ? 'infoBox-value' : ''}>{siteInfoData?.sDate && siteInfoData?.fDate ? `${siteInfoData?.sDate} ~ ${siteInfoData?.fDate}` : ''}</div>
                {isSiteDashboard && t('공사 기간')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
